import { FC } from 'react';
import Modal from '@new/common/ui/Modal/Modal';
import { Button } from '@new/common/ui';
import { CourseNameList } from './CourseNameList';

type CourseActionType = 'delete' | 'start' | 'stop';

interface ConfirmCourseModalProps {
  isOpen: boolean;
  action: CourseActionType;
  courseNames: string[];
  isPending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const getActionContent = (action: CourseActionType, count: number) => {
  const isBulk = count > 1;

  if (action === 'delete') {
    return {
      title: isBulk ? `Remove ${count} Courses` : 'Remove Course',
      question: isBulk
        ? `Are you sure you want to remove the following ${count} courses?`
        : 'Are you sure you want to remove this course?',
      description: 'This action cannot be undone.',
      buttonText: isBulk ? `Remove ${count} Courses` : 'Remove Course',
      pendingText: 'Removing...',
      buttonClassName: 'bg-(--danger-D700)! hover:opacity-90 text-white',
      maxHeight: isBulk ? 'h-[480px]' : 'h-[320px]',
    };
  }

  const label = action === 'start' ? 'Start' : 'Reset';

  return {
    title: isBulk ? `${label} ${count} Courses` : `${label} Course`,
    question: isBulk
      ? `Are you sure you want to ${action} the following ${count} courses?`
      : `Are you sure you want to ${action} this course?`,
    description:
      action === 'stop'
        ? 'This will reset the course progress to initial level. This action cannot be undone.'
        : `This will mark the course${isBulk ? 's' : ''} as started.`,
    buttonText: `${label} Course${isBulk ? 's' : ''}`,
    pendingText: action === 'start' ? 'Starting...' : 'Resetting...',
    buttonClassName: undefined,
    maxHeight: isBulk ? 'h-[500px]' : 'h-[320px]',
  };
};

export const ConfirmCourseModal: FC<ConfirmCourseModalProps> = ({
  isOpen,
  action,
  courseNames,
  isPending,
  onConfirm,
  onClose,
}) => {
  const isBulk = courseNames.length > 1;
  const content = getActionContent(action, courseNames.length);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={content.title}
      className={`w-112.5 ${content.maxHeight}`}
    >
      <div className="flex h-full flex-col justify-between gap-4">
        <div className="flex flex-col gap-2">
          <p className="[font-family:var(--font-montserrat)] text-sm text-(--neutral-black-NB1000) m-0">
            {content.question}
          </p>

          {isBulk && <CourseNameList names={courseNames} />}

          <p className="text-sm text-(--neutral-black-NB700) m-0">{content.description}</p>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={isPending}
            className={content.buttonClassName}
          >
            {isPending ? content.pendingText : content.buttonText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
